import { AxiosError, AxiosRequestConfig } from 'axios';
import { BASE_BACKEND_URL, GRAPHQL_BASE_BACKEND_URL } from 'env';
import i18next from 'i18next';

import { ILink } from '@pricemoov-oss/moon';
import axiosClientFactory from '@pricemoov-oss/moon-axios';
import graphQLClientFactory from '@pricemoov-oss/moon-graphql';
import { notifyAlert } from '@pricemoov/pollux-react';

import { PRICEMOOV_AUTH_KEY } from 'redesign/helpers/auth';
import localStorage from 'redesign/helpers/localStorage';

export enum HTTPStatus {
    Ok = 200,
    BadRequest = 400,
    Unauthorized = 401,
    Forbidden = 403,
    Conflict = 409,
    NotFound = 404,
    UrlTooBig = 414,
    UnprocessableEntity = 422,
}

export const REDIRECT_TO_NOT_FOUND_ERRORS = [HTTPStatus.Forbidden, HTTPStatus.NotFound];

// Client-side request timeout (ms). Bounds a hung/severed backend request so the UI
// shows an error + retry instead of an endless loading skeleton. The worst legitimate
// prices-view load observed is ~100s, so 120s leaves margin while still catching hangs.
export const REST_TIMEOUT_MS = 120_000;

// Timeout of the file exports (ms). The BE builds an export in one synchronous request and
// axios counts its timeout over the entire download, so a large export needs several minutes,
// far beyond REST_TIMEOUT_MS. Bounded override until exports move to the async job flow.
export const EXPORT_TIMEOUT_MS = 600_000;

function errorHandler(error: AxiosError) {
    if (error?.response?.status === HTTPStatus.UrlTooBig) {
        notifyAlert({
            level: 'error',
            title: i18next.t('url_too_big.error') as string,
        });
    }
    return Promise.reject(error);
}

function setApiKey(config: AxiosRequestConfig): AxiosRequestConfig | Promise<AxiosRequestConfig> {
    return {
        ...config,
        headers: {
            ...config.headers,
            ...(process.env.API_KEY != null && { 'X-Api-Key': process.env.API_KEY }),
        },
    };
}

function isAbsoluteURL(url: string) {
    return /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url);
}

export const moonRetry = (failureCount: number, err: AxiosError) => {
    const error = err as AxiosError;
    // Disables retries:
    //    0. on a client-side timeout (REST_TIMEOUT_MS): the wait is already bounded,
    //       retrying would only multiply the time before the user sees an error.
    //    1. on timeout from the BE
    //    2. if the request performed by the user is wrong (status in the 400s)
    // axios reports a client-side timeout as ECONNABORTED, or ETIMEDOUT when
    // transitional.clarifyTimeoutError is enabled / depending on the version.
    if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') {
        return false;
    }
    if (
        error?.response?.status === 504 ||
        (error?.response?.status >= 400 && error?.response?.status < 500)
    ) {
        return false;
    }
    return failureCount <= 2;
};

export function getMoonConfig(language = 'en'): ILink[] {
    function setLanguage(
        config: AxiosRequestConfig
    ): AxiosRequestConfig | Promise<AxiosRequestConfig> {
        return {
            ...config,
            headers: {
                ...config.headers,
                'Accept-Language': language,
            },
            withCredentials: true,
        };
    }

    function setToken(
        config: AxiosRequestConfig
    ): AxiosRequestConfig | Promise<AxiosRequestConfig> {
        const pricemoovAuth = localStorage.get(PRICEMOOV_AUTH_KEY);
        const headers = {
            'Content-Type': 'application/json',
            ...config.headers,
        };
        let userToken;
        if (pricemoovAuth && !isAbsoluteURL(config.url)) {
            // Add Authorization only for backend url, ie when using baseUrl
            userToken = JSON.parse(pricemoovAuth).token;
            // Add Authorization only if the user token is defined
            if (userToken) {
                headers.Authorization = `jwt ${userToken}`;
            }
        }

        return {
            ...config,
            headers,
        };
    }

    const responseInterceptors = [{ onRejected: errorHandler }];
    const requestInterceptors = [
        { onFulfilled: setToken },
        { onFulfilled: setLanguage },
        { onFulfilled: setApiKey },
    ];

    return [
        {
            id: 'rest',
            config: { baseURL: BASE_BACKEND_URL, timeout: REST_TIMEOUT_MS },
            interceptors: { request: requestInterceptors, response: responseInterceptors },
            clientFactory: axiosClientFactory,
        },
        {
            id: 'graphql',
            config: { baseURL: GRAPHQL_BASE_BACKEND_URL },
            interceptors: { request: requestInterceptors, response: responseInterceptors },
            clientFactory: graphQLClientFactory,
        },
    ];
}
